import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Provider,
  AliasType,
  useDeleteReservePaymentMutation,
  useSendReservePaymentMutation,
  EntityEdge,
  AliasEdge,
  ClaimantEdge,
} from '@converge/graphql';
import { DialogLayout } from '../../../../presentation';
import { NewConvergeButton } from '../../../../presentation/components/new-button';
import './claim-reserve-table.scss';
import './expanded-payment-row.scss';
import './payment-delete-modal.scss';
// Import types locally to avoid conflicts with GraphQL types
interface LocalReservePayment {
  id: string;
  reserveId: string;
  date: string;
  createdAt?: string | null; // Date initiated
  paidAt?: string | null; // Date paid
  retentionAmount?: number | null; // Retention amount
  reserveAmount?: number | null; // Reserve amount
  invoiceNumber?: string | null; // Invoice number
  status: string;
  paymentMethod: string;
  notes?: string;
  payee?: string | null;
  vendor?: EntityEdge; // Vendor object with nested structure
  insured?: EntityEdge | null; // insured object with nested structure
  // Display properties
  displayPayee?: string;
  claimant?: ClaimantEdge | null;
}

interface DateFieldProps {
  paymentDate?: string | null;
  invoiceDate?: string | null;
}

interface AmountFieldProps {
  retentionAmount?: number | null;
  reserveAmount?: number | null;
  formatCurrency: (amount: number) => string;
}

interface PayeeFieldProps {
  vendor?: EntityEdge;
  insured?: EntityEdge | null;
  invoiceNumber?: string | null;
  claimant?: ClaimantEdge | null;
}

const DateField: React.FC<DateFieldProps> = ({ paymentDate, invoiceDate }) => {
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', mr: 3 }}
      className="date-field-container"
    >
      <Box className="date-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          DATE INITIATED
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {formatDate(paymentDate)}
        </Typography>
      </Box>
      <Box className="date-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          DATE PAID
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {formatDate(invoiceDate)}
        </Typography>
      </Box>
    </Box>
  );
};

const AmountField: React.FC<AmountFieldProps> = ({
  retentionAmount,
  reserveAmount,
  formatCurrency,
}) => {
  const formatAmount = (amount?: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return formatCurrency(amount);
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', mr: 3 }}
      className="amount-field-container"
    >
      <Box className="amount-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          RETENTION AMOUNT
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {formatAmount(retentionAmount)}
        </Typography>
      </Box>
      <Box className="amount-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          RESERVE AMOUNT
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {formatAmount(reserveAmount)}
        </Typography>
      </Box>
    </Box>
  );
};

const PayeeField: React.FC<PayeeFieldProps> = ({
  vendor,
  invoiceNumber,
  insured,
  claimant,
}) => {
  const getPayeeName = () => {
    if (claimant?.node?.uuid) {
      return `${claimant.node.firstName} ${claimant.node.lastName}`;
    }
    if (!vendor?.node?.aliases?.edges?.length) {
      return insured?.node?.aliases?.edges.filter(
        (item: AliasEdge) =>
          item?.node?.sources?.includes(Provider.Primary) &&
          item?.node?.type === AliasType.LegalName
      )[0]?.node?.name;
    }

    const primaryLegalName = vendor.node.aliases.edges.filter(
      (item: AliasEdge) =>
        item?.node?.sources?.includes(Provider.Primary) &&
        item?.node?.type === AliasType.LegalName
    )[0]?.node?.name;

    return primaryLegalName || 'N/A';
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', mr: 0 }}
      className="payee-field-container"
    >
      <Box className="payee-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          PAYEE
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {getPayeeName()}
        </Typography>
      </Box>
      <Box className="payee-field-item">
        <Typography className="expanded-row-field-label expanded-row-field-label-responsive">
          INVOICE NUMBER
        </Typography>
        <Typography
          variant="body2"
          className="expanded-row-field-value expanded-row-field-value-responsive"
        >
          {invoiceNumber || 'N/A'}
        </Typography>
      </Box>
    </Box>
  );
};

// Status pill component
const StatusPill: React.FC<{ status: string; isNotActive: boolean }> = ({
  status,
  isNotActive,
}) => {
  const getStatusInfo = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return {
          backgroundColor: '#1976d2',
          color: 'white',
          text: 'PAYMENT COMPLETE',
          opacity: isNotActive ? '0.4' : '1',
        };
      case 'PENDING':
        return {
          backgroundColor: '#ef6c00',
          color: 'white',
          text: 'PAYMENT PENDING',
          opacity: isNotActive ? '.4' : '1',
        };
      case 'OPEN':
        // OPEN status won't be displayed, but included for completeness
        return {
          backgroundColor: '#4caf50',
          color: 'white',
          opacity: isNotActive ? '.4' : '1',
        };
      default:
        return {
          backgroundColor: '#e0e0e0',
          color: '#424242',
          opacity: isNotActive ? '.4' : '1',
        };
    }
  };

  const statusColors = getStatusInfo(status);

  return (
    <Chip
      label={statusColors.text ?? (status?.toUpperCase() || 'UNKNOWN')}
      size="small"
      sx={{
        backgroundColor: statusColors.backgroundColor,
        color: statusColors.color,
        fontWeight: 600,
        height: '20px',
        '& .MuiChip-label': {
          padding: '0 6px',
        },
        padding: '16px 18px',
        fontSize: '14px',
        opacity: statusColors.opacity,
      }}
    />
  );
};

export interface EnhancedPayment extends LocalReservePayment {
  // All properties already defined in LocalReservePayment
}

interface ExpandedPaymentRowProps {
  payments: EnhancedPayment[];
  formatCurrency?: (amount: number) => string;
  onPayNow?: (paymentId: string) => void;
  onUpdatePayment?: (paymentId: string) => void;
  onRefreshData?: () => void; // Callback to refresh data after delete/update
  reserveUuid: string; // UUID of the parent reserve
  disablePayNow?: boolean; // Disable SEND FOR PAYMENT button when no reserveUuid
  isNotActive: boolean;
}

const ExpandedPaymentRow: React.FC<ExpandedPaymentRowProps> = ({
  payments,
  formatCurrency = (amount) => `$${amount.toLocaleString()}`,
  onPayNow,
  onUpdatePayment,
  onRefreshData,
  reserveUuid,
  disablePayNow = false,
  isNotActive,
}) => {
  // State for dialog
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [payingPaymentId, setPayingPaymentId] = useState<string>('');
  const [payConfirmDialogOpen, setPayConfirmDialogOpen] = useState(false);
  const [selectedPaymentToPay, setSelectedPaymentToPay] =
    useState<EnhancedPayment | null>(null);

  // Delete mutation
  const { deleteReservePayment, loading: deleteLoading } =
    useDeleteReservePaymentMutation();

  // Pay mutation
  const { sendReservePayment, loading: payLoading } =
    useSendReservePaymentMutation();

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handlePayCancel = () => {
    setPayConfirmDialogOpen(false);
    setSelectedPaymentToPay(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteReservePayment({
        variables: {
          paymentUuid: selectedPaymentId,
          reserveUuid: reserveUuid,
        },
      });

      // Call refresh callback if provided
      if (onRefreshData) {
        onRefreshData();
      }

      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const handlePayNow = (payment: EnhancedPayment) => {
    setSelectedPaymentToPay(payment);
    setPayConfirmDialogOpen(true);
  };

  const handlePayConfirm = async () => {
    if (!selectedPaymentToPay) return;

    setPayingPaymentId(selectedPaymentToPay.id);
    try {
      await sendReservePayment({
        variables: {
          reserveUuid: reserveUuid,
          reservePaymentInput: {
            paymentUuid: selectedPaymentToPay.id,
          },
        },
      });

      // Call refresh callback if provided
      if (onRefreshData) {
        onRefreshData();
      }

      setPayConfirmDialogOpen(false);
      setSelectedPaymentToPay(null);
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setPayingPaymentId('');
    }
  };

  // Payment actions
  const paymentActions = [
    {
      label: 'Update Payment',
      onClick: (paymentId: string) => {
        if (onUpdatePayment) {
          onUpdatePayment(paymentId);
        }
      },
    },
    {
      label: 'Delete Payment',
      onClick: (paymentId: string) => {
        setSelectedPaymentId(paymentId);
        setDeleteDialogOpen(true);
      },
      color: 'error.main',
    },
  ];

  // Payment tooltip component - styled like PolicyTooltip
  const PaymentTooltip = (payment: EnhancedPayment) => (
    <Tooltip
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'white',
            color: 'black',
            boxShadow: 1,
            border: '1px solid rgba(0,0,0,0.12)',
            maxWidth: 'none',
            p: 0,
          },
        },
      }}
      title={
        <Box
          sx={{
            backgroundColor: 'white',
            color: 'black',
            margin: 0,
            padding: 0,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              px: 2,
              py: 1,
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: '12px',
              color: '#666',
            }}
          >
            Select Action
          </Typography>
          <Divider />
          <List dense disablePadding sx={{ maxHeight: 240, overflowY: 'auto' }}>
            {paymentActions.map((action, idx) => {
              const isDisabled = payment.status?.toUpperCase() !== 'OPEN';
              return (
                <ListItemButton
                  key={idx}
                  onClick={() => !isDisabled && action.onClick(payment.id)}
                  disabled={isDisabled}
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: isDisabled ? '#9e9e9e' : 'black',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    '&:hover': {
                      backgroundColor: isDisabled ? 'transparent' : '#CEFCF6',
                      color: isDisabled ? '#9e9e9e' : 'black',
                    },
                    '&.Mui-selected': {
                      backgroundColor: isDisabled ? 'transparent' : '#CEFCF6',
                      color: isDisabled ? '#9e9e9e' : 'black',
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : '#CEFCF6',
                      },
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ListItemText primary={action.label} />
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      }
    >
      <MoreVertIcon style={{ cursor: 'pointer' }} />
    </Tooltip>
  );

  if (!payments || payments.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">
          No payment records found for this reserve.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="expanded-payment-row">
      <Table
        size="small"
        aria-label="payment details"
        className="reserve-payment-table"
      >
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell sx={{ width: '21%' }} className="date-field-cell">
                <DateField
                  paymentDate={payment.createdAt}
                  invoiceDate={payment.paidAt}
                />
              </TableCell>
              <TableCell sx={{ width: '21%' }} className="amount-field-cell">
                <AmountField
                  retentionAmount={payment.retentionAmount}
                  reserveAmount={payment.reserveAmount}
                  formatCurrency={formatCurrency}
                />
              </TableCell>
              <TableCell sx={{ width: '30%' }} className="payee-field-cell">
                <PayeeField
                  vendor={payment.vendor}
                  invoiceNumber={payment.invoiceNumber}
                  insured={payment.insured}
                  claimant={payment.claimant}
                />
              </TableCell>
              <TableCell
                sx={{
                  cursor: isNotActive ? 'default' : 'pointer',
                  pointerEvents: isNotActive ? 'none' : 'auto !important',
                }}
              >
                {payment.status?.toUpperCase() === 'OPEN' ? (
                  <NewConvergeButton
                    label={'SEND FOR PAYMENT'}
                    variant="outlined"
                    size="small"
                    onClick={() => handlePayNow(payment)}
                    className={`${
                      isNotActive
                        ? 'add-payment-button-disabled'
                        : 'add-payment-button'
                    }`}
                    loading={payLoading && payingPaymentId === payment.id}
                    disabled={
                      disablePayNow ||
                      (payLoading && payingPaymentId === payment.id)
                    }
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* <Typography className="expanded-row-field-label">Status</Typography> */}
                    <Box sx={{ mt: 0 }}>
                      <StatusPill
                        status={payment.status}
                        isNotActive={isNotActive}
                      />
                    </Box>
                  </Box>
                )}
              </TableCell>
              <TableCell
                sx={{
                  width: '60px',
                  padding: '0 8px',
                  cursor: isNotActive ? 'default' : 'pointer',
                  pointerEvents: isNotActive ? 'none' : 'auto !important',
                  color: isNotActive ? 'rgba(0, 0, 0, 0.26)' : '',
                }}
              >
                {PaymentTooltip(payment)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Payment Options Menu has been replaced by PaymentTooltip */}

      {/* Delete Confirmation Dialog */}
      <DialogLayout
        data-testid="delete-payment-modal"
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        showCloseButton={true}
        onCloseIconClick={handleDeleteCancel}
        header={
          <Box className="modal-title-delete">Delete Payment Information</Box>
        }
      >
        <Stack className="content-message-license">
          Are you sure you want to delete this payment? This action cannot be
          undone.
        </Stack>
        <Stack className="modal-btn-container-message-license">
          <Box style={{ marginRight: '15px' }}>
            <NewConvergeButton
              label="CANCEL"
              variant="outlined"
              onClick={handleDeleteCancel}
            />
          </Box>
          <Box>
            <NewConvergeButton
              label={deleteLoading ? 'DELETING...' : 'DELETE PAYMENT'}
              loading={deleteLoading}
              onClick={handleDeleteConfirm}
            />
          </Box>
        </Stack>
      </DialogLayout>

      {/* Pay Confirmation Dialog */}
      <DialogLayout
        data-testid="pay-payment-modal"
        open={payConfirmDialogOpen}
        onClose={handlePayCancel}
        showCloseButton={true}
        onCloseIconClick={handlePayCancel}
        header={<Box className="modal-title-delete">Confirm Payment</Box>}
      >
        <Stack className="content-message-license">
          Are you sure you want to initiate this payment? This action cannot be
          undone.
        </Stack>
        <Stack className="modal-btn-container-message-license">
          <Box style={{ marginRight: '15px' }}>
            <NewConvergeButton
              label="CANCEL"
              variant="outlined"
              onClick={handlePayCancel}
            />
          </Box>
          <Box>
            <NewConvergeButton
              label={'CONFIRM'}
              loading={payLoading}
              onClick={handlePayConfirm}
            />
          </Box>
        </Stack>
      </DialogLayout>
    </Box>
  );
};

export default ExpandedPaymentRow;
